import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { createCoreSlice } from './slices/coreSlice';
import { createFleetSlice } from './slices/fleetSlice';
import { createHRSlice } from './slices/hrSlice';
import { createContractSlice } from './slices/contractSlice';
import { createFinanceSlice } from './slices/financeSlice';
import { createEventSlice } from './slices/eventSlice';
import { createUISlice } from './slices/uiSlice';
import { createTaxSlice } from './slices/taxSlice';
import { createBankSlice } from './slices/bankSlice';
import { createSupportSlice } from './slices/supportSlice';
import { createMarketIntelSlice } from './slices/marketIntelSlice';
import { createAuditSlice } from './slices/auditSlice';
import { createMessageSlice } from './slices/messageSlice';
import { createAcquisitionSlice } from './slices/acquisitionSlice';
import { createCrmSlice } from './slices/crmSlice';
import { createOfficeSlice } from './slices/officeSlice';
import { createLeaseAidSlice } from './slices/leaseAidSlice';
import { createAchievementsSlice } from './slices/achievementsSlice';
import { createTenderSlice } from './slices/tenderSlice';
import { createSpotEngagementSlice } from './slices/spotEngagementSlice';
import { createCommercialOpportunitySlice } from './slices/commercialOpportunitySlice';
import { createDepotManagerSlice } from './slices/depotManagerSlice';
import { createContractHistorySlice } from './slices/contractHistorySlice';
import { createRelationEventsSlice } from './slices/relationEventsSlice';
import { createLegalProceduresSlice } from './slices/legalProceduresSlice';
import { createDriverPoolSlice } from './slices/driverPoolSlice';
import { createConvoyExceptionnelSlice } from './slices/convoyExceptionnelSlice';
import { createWorkAccidentSlice } from './slices/workAccidentSlice';
import { createMarketingSalonSlice } from './slices/marketingSalonSlice';
import { createPedagogySlice } from './slices/pedagogySlice';
import { createScenarioPushSlice } from './slices/scenarioPushSlice';
import { sanitizeCredits, hasCorruptCredit } from './helpers/sanitizeCredits';

const useGameStore = create(
  persist(
    (...a) => ({
      ...createCoreSlice(...a),
      ...createFleetSlice(...a),
      ...createHRSlice(...a),
      ...createContractSlice(...a),
      ...createFinanceSlice(...a),
      ...createEventSlice(...a),
      ...createUISlice(...a),
      ...createTaxSlice(...a),
      ...createBankSlice(...a),
      ...createSupportSlice(...a),
      ...createMarketIntelSlice(...a),
      ...createAuditSlice(...a),
      ...createMessageSlice(...a),
      ...createAcquisitionSlice(...a),
      ...createCrmSlice(...a),
      ...createOfficeSlice(...a),
      ...createLeaseAidSlice(...a),
      ...createAchievementsSlice(...a),
      ...createTenderSlice(...a),
      ...createSpotEngagementSlice(...a),
      ...createCommercialOpportunitySlice(...a),
      ...createDepotManagerSlice(...a),
      ...createContractHistorySlice(...a),
      ...createRelationEventsSlice(...a),
      ...createLegalProceduresSlice(...a),
      ...createDriverPoolSlice(...a),
      ...createConvoyExceptionnelSlice(...a),
      ...createWorkAccidentSlice(...a),
      ...createMarketingSalonSlice(...a),
      ...createPedagogySlice(...a),
      ...createScenarioPushSlice(...a),
    }),
    {
      name: 'hst-game-save',
      version: 2, // 04/02/2026 — Refonte F1-F4 (formations, chauffeurs, flotte, dépôt) : v1 saves obsolètes.
      storage: createJSONStorage(() => localStorage),
      // 04/06/2026 — sanitize the persisted `credits` array on rehydrate so
      // legacy saves containing `NaN`/`undefined`/`null` numeric fields
      // (reported as a production bug — total debt + Restant dû displaying
      // "NaN €") don't propagate corrupt math through every reduce.
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        if (Array.isArray(state.credits) && hasCorruptCredit(state.credits)) {
          console.warn('[hst-game-save] sanitizing corrupt credits on rehydrate');
          state.credits = sanitizeCredits(state.credits);
        }
        // 17/06/2026 — Migration des chauffeurs avec `level` string (issus
        // de rachats sur d'anciennes versions). Cf. utils/driverLevel.js.
        if (Array.isArray(state.drivers)) {
          // eslint-disable-next-line global-require
          const { migrateDriversLevel } = require('../utils/driverLevel');
          state.drivers = migrateDriversLevel(state.drivers);
        }
        // 25/07/2026 — Retour user prod « dépôt saturé 5 places » : la
        // capacité de base passe de 5 à 10 → remise à niveau des dépôts
        // persistés (cf. helpers/migrateDepotCapacities.js).
        if (Array.isArray(state.ownedDepots) && state.ownedDepots.length > 0) {
          // eslint-disable-next-line global-require
          const { migrateDepotCapacities } = require('./helpers/migrateDepotCapacities');
          state.ownedDepots = migrateDepotCapacities(state.ownedDepots);
        }
        // 28/07/2026 — Bug prod AO « Mairie de Tours 48 240 €/j » : répare
        // les contrats AO dont la valeur totale a été appliquée en gain
        // journalier. Cf. helpers/repairTenderContracts.js.
        {
          // eslint-disable-next-line global-require
          const { repairTenderContractsInState } = require('./helpers/repairTenderContracts');
          const repaired = repairTenderContractsInState(state);
          if (repaired > 0) {
            console.warn(`[hst-game-save] ${repaired} contrat(s) AO réparé(s) — valeur totale appliquée par jour`);
          }
        }
        // 28/07/2026 — Recrutement local conditionné à un dépôt dans le
        // pays : les chauffeurs locaux orphelins (pays sans dépôt) quittent
        // l'effectif. Cf. helpers/pruneLocalDrivers.js.
        if (Array.isArray(state.drivers) && state.drivers.some(d => d && d.localCountry)) {
          // eslint-disable-next-line global-require
          const { pruneLocalDriversWithoutDepot } = require('./helpers/pruneLocalDrivers');
          const pruned = pruneLocalDriversWithoutDepot(state);
          if (pruned) {
            console.warn(`[hst-game-save] ${pruned.removed.length} chauffeur(s) local(aux) retiré(s) — aucun dépôt dans leur pays`);
            state.drivers = pruned.drivers;
            state.vehicles = pruned.vehicles;
            state.notifications = [
              ...(state.notifications || []).slice(-30),
              {
                id: `local-prune-${Date.now()}`,
                type: 'driver_left',
                title: 'Départ de chauffeurs locaux',
                message: `${pruned.removed.map(d => d.name).join(', ')} : sans dépôt ouvert dans leur pays, ces chauffeurs locaux ont quitté l'effectif.`,
                day: state.totalDays || 0,
                read: false,
              },
            ];
          }
        }
        // 11/02/2026 — Retour user prod : « bug message mixés ». Scan
        // défensif de l'inbox persistée : détecte les messages dont le
        // body contient des fragments d'autres templates (thanks + tender
        // mélangés) ou un placeholder résiduel non substitué, et tente
        // de les réparer en re-instanciant depuis le template d'origine.
        // Voir helpers/inboxIntegrity.js.
        if (Array.isArray(state.inbox) && state.inbox.length > 0) {
          // eslint-disable-next-line global-require
          const { repairInbox } = require('./helpers/inboxIntegrity');
          const companyName = state.company?.name || 'HST';
          const { repairedInbox, repairedCount } = repairInbox(state.inbox, companyName);
          if (repairedCount > 0) {
            console.warn(`[hst-game-save] inbox integrity : ${repairedCount} message(s) corrupted → repaired on rehydrate`);
            state.inbox = repairedInbox;
          }
        }
      },
      // 04/02/2026 — Phase F1-F4 : refonte profonde des spés/formations/flotte/dépôt.
      // Les saves v1 ne sont plus structurellement compatibles. Marque la save
      // comme "legacy" pour que l'app affiche une bannière d'invitation à
      // recommencer une partie (cf. LegacySaveBanner.jsx). Pas de migration
      // automatique — choix utilisateur validé.
      migrate: (persistedState, version) => {
        if (version < 2 && persistedState && typeof persistedState === 'object') {
          return { ...persistedState, __legacySave: true };
        }
        return persistedState;
      },
    }
  )
);

// Dev / test escape hatch — expose the store on window so E2E test agents
// (Playwright) can poke at state directly: window.__hstStore.getState() /
// .setState() / .getState().pushMessageFromTemplate() etc. NEVER exposed in
// production builds.
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  window.__hstStore = useGameStore;
}

export default useGameStore;

